//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { themr, type ThemeType } from 'react-css-themr';
import { ButtonWithPopover } from 'common/components/core/button';
import { singleSlotSelectorFactory } from 'common/entity/components/slot/SingleSlotSelector';
import { singleSlotSelectorFactoryWithOffset } from 'common/entity/components/slot/SingleSlotSelectorWithOffset';
import BreakShiftDialog from 'common/entity/components/dayShift/BreakShiftDialog';
import BreakShiftDialogWithOffset from 'common/entity/components/dayShift/BreakShiftDialogWithOffset';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';
import theme from './SingleSlotSelectorWithBreaks.scss';
import type { TFunction } from 'react-i18next';
import type moment from 'moment-timezone';

type Props = {
    theme: ThemeType,
    index: number | string,
    breaks: Immutable.List<any>,
    showBreaksDialog: boolean,
    breakSlotFor?: string,
    slotFor: string,
    t: TFunction,
    disabled: boolean,
    closeBreaksDialog: () => void,
    onChange: ({ breaks: Array<Object>, slotFor: string }) => void,
    onAddBreakClick: ({ slotFor: string, index: number | string }) => void,
    isBreaksWithOffset: boolean,
    start: moment,
    end: moment,
    endDayOffset: number,
    timeFormat: ?string
};

class Actions extends React.PureComponent<Props> {
    onAddBreakClick = (...args) => {
        const { slotFor, index, onAddBreakClick } = this.props;
        onAddBreakClick({ slotFor, index }, ...args);
    };

    render() {
        const {
            onAddBreakClick,
            theme,
            breaks,
            showBreaksDialog,
            breakSlotFor,
            slotFor,
            t,
            disabled,
            closeBreaksDialog,
            onChange,
            isBreaksWithOffset,
            start,
            end,
            endDayOffset,
            timeFormat
        } = this.props;
        const noOfBreaks = breaks && breaks.size;
        const breaksPopover =
            noOfBreaks > 0 ? (
                <div>
                    {breaks.map((mBreak, index) => {
                        return (
                            <div key={index}>
                                {isBreaksWithOffset
                                    ? mBreak.getBreakWithOffsetDescription(
                                          slotFor
                                      )
                                    : mBreak.getBreakDescription()}
                            </div>
                        );
                    })}
                </div>
            ) : null;
        const breaksLabel =
            noOfBreaks > 0
                ? t('configure:breakCount_interval', {
                      postProcess: 'interval',
                      count: noOfBreaks
                  })
                : `+ ${t('translations:break')}`;
        const shouldShowBreakShiftDialog =
            showBreaksDialog && breakSlotFor === slotFor && !isBreaksWithOffset;
        const shouldShowBreakShiftDialogWithOffset =
            showBreaksDialog && breakSlotFor === slotFor && isBreaksWithOffset;
        return onAddBreakClick ? (
            <div className={theme.breaksContainer}>
                <ButtonWithPopover
                    label={breaksLabel}
                    className={theme.addBreakBtn}
                    theme={theme}
                    onClick={this.onAddBreakClick}
                    popOverContent={breaksPopover}
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    semibold
                    uppercase
                    ripple={false}
                    disabled={disabled}
                />
                {shouldShowBreakShiftDialog ? (
                    <BreakShiftDialog
                        breakSlotFor={breakSlotFor}
                        defaultBreaks={breaks}
                        closeBreaksDialog={closeBreaksDialog}
                        onChange={onChange}
                        isBreaksWithOffset={isBreaksWithOffset}
                        shiftStart={start}
                        shiftEnd={end}
                        endDayOffset={endDayOffset}
                    />
                ) : null}
                {shouldShowBreakShiftDialogWithOffset ? (
                    <BreakShiftDialogWithOffset
                        breakSlotFor={breakSlotFor}
                        defaultBreaks={breaks}
                        closeBreaksDialog={closeBreaksDialog}
                        onChange={onChange}
                        isBreaksWithOffset={isBreaksWithOffset}
                        shiftStart={start}
                        shiftEnd={end}
                        endDayOffset={endDayOffset}
                        timeFormat={timeFormat}
                    />
                ) : null}
            </div>
        ) : null;
    }
}
export const SingleSlotSelectorWithBreaks = singleSlotSelectorFactory({
    Actions
});

export const SingleSlotSelectorWithBreaksOffset = singleSlotSelectorFactoryWithOffset(
    {
        Actions
    }
);

export default themr(
    'SingleSlotSelectorWithBreaks',
    theme
)(SingleSlotSelectorWithBreaks);
